/**
 * The app's routes and the providers around them (the part of the app a test can draw).
 *
 * Public pages that need no account sit outside the layout: the account
 * deletion page, the pause reminder's one-tap cancel (/billing/stop), the
 * gift pages (/gift to buy a year of Grow More, /gift/thanks after paying,
 * /gift/redeem to use a code), the
 * Monday garden email's monthly and unsubscribe links (/email/monday), a
 * share link's page (/s/:code), a gardener's garden page (/g/:code, or
 * the gardener's own address, /@:slug) and Gardens on Grow, the public listing
 * of gardens (/gardens). Everything else is
 * inside Layout, behind sign-in, and the app opens on Today.
 *
 * A garden address is /@amy. React Router 6 matches whole segments only, so
 * the route is /:handle, ranked below every named path, and GardenPage takes
 * only a segment starting with "@"; any other (/amy, a typo) it sends to
 * Today, as the catch-all does. No app page starts with "@", so none can be
 * taken. An address no one has goes on to Today too. A public plant's or
 * bed's own page, /@amy/cherokee-purple, is /:handle/:item in the same way
 * (GardenItemPage).
 *
 * An address that matches no route goes to Today instead of rendering an
 * empty page; /seasons and /photos, the addresses people type for the
 * Seasons tab and Photo sort, go to /season and /plants/photos.
 *
 * Providers, outermost first: QueryClientProvider (the one React Query
 * cache, config/queryClient), GlobalStyle, the router, and AuthProvider
 * (Firebase and the Grow account) around every route, public ones too, so a
 * public page can say "Signed in as". The signed-in pages add, in order,
 * UserConfigProvider (preferences and language), ThemeProvider,
 * WeatherProvider, TooltipProvider and then Layout, which shows the sign-in
 * screen (or the end of signing up) instead of the page when nobody is
 * signed in. Each page then gets only the data providers it reads:
 * StagesProvider, PlantsProvider, LocationsProvider. A provider mounted per
 * route is remounted when the route changes, so moving between pages reads
 * from the query cache rather than from provider state.
 *
 * No route needs a plan. What a plan unlocks is enforced per request by the
 * backend (402 SUBSCRIPTION_REQUIRED and friends, answered by the
 * SubscriptionWall in Layout), not by hiding pages. /admin/gallery is the one
 * page for a few people: anyone may open it, and GalleryQueue says "not for
 * you" unless the backend says they approve photos.
 *
 * Used by: App.tsx (which adds the query cache, the style sheet and the browser router around it) and
 * src/test-support/renderRoute.mjs (which adds a test query cache and a static router, so a test draws a real route
 * the way the app builds it). The full route table, with each screen's file, is
 * in private/docs-from-bloom-next/APP-ARCHITECTURE.md; keep the two in step when a route changes.
 *
 * Env vars: none read here.
 *
 * Surprising: the future flag v7_relativeSplatPath is on, so React Router 6
 * already resolves relative links under a splat route ("*") the way version
 * 7 will.
 *
 * @module AppRoutes
 */


import { Routes, Route, Navigate } from 'react-router-dom';
import { PlantsProvider } from '@/shared/context/PlantsContext';
import { LocationsProvider } from '@/shared/context/LocationsContext';
import { StagesProvider } from '@/shared/context/StagesContext';
import { WeatherProvider } from '@/shared/context/WeatherContext';
import { AuthProvider } from '@/shared/context/AuthContext';
import { UserConfigProvider } from '@/shared/context/UserConfigProvider';
import { ThemeProvider } from '@/shared/context/ThemeProvider';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Layout } from '@/shared/components/layout/Layout';
import { PlantGrid } from '@/features/plant-list/components/PlantGrid';
import { LocationGrid } from '@/features/locations/components/LocationGrid';
import { ArchivedLocations } from '@/features/locations/components/ArchivedLocations';
import { LocationsRedirect } from '@/features/locations/components/LocationsRedirect';
import { PhotoTray } from '@/features/photo-tray/PhotoTray';
import { WhatToPlantPage } from '@/features/what-to-plant/WhatToPlantPage';
import { WhatToPlantBrowsePage } from '@/features/what-to-plant/WhatToPlantBrowsePage';
import { SubscribePage } from '@/features/billing/SubscribePage';
import { SubscribeSuccess } from '@/features/billing/SubscribeSuccess';
import { StopSubscriptionPage } from '@/features/billing/StopSubscriptionPage';
import { GiftPage } from '@/features/gift/GiftPage';
import { GiftThanksPage } from '@/features/gift/GiftThanksPage';
import { GiftRedeemPage } from '@/features/gift/GiftRedeemPage';
import { MondayEmailLinkPage } from '@/features/email/MondayEmailLinkPage';
import { GalleryQueue } from '@/features/gallery/GalleryQueue';
import { ViewAsPage } from '@/features/admin/ViewAsPage';
import { JoinGarden } from '@/features/garden-members/JoinGarden';
import { WeatherPage } from '@/features/weather/components/WeatherPage';
import { SeasonPage } from '@/features/season/SeasonPage';
import { SeasonReviewPage } from '@/features/season/SeasonReviewPage';
import { TodayPage } from '@/features/today/TodayPage';
import { UserProfile } from '@/user/UserProfile';
import { ArchivedPlants } from '@/features/plant-list/components/ArchivedPlants';
import { DeleteAccountPage } from '@/user/DeleteAccountPage';
import { SharePage } from '@/features/share/SharePage';
import { GardenPage } from '@/features/share/GardenPage';
import { GardensPage } from '@/features/share/GardensPage';
import { GardenItemPage } from '@/features/share/GardenItemPage';


/**
 * Every route of the app, inside AuthProvider, each page inside the providers it reads. Draw it inside a query cache and a
 * router (App does with the browser router, a test with a static one).
 *
 * @returns The routes.
 */
export function AppRoutes(): JSX.Element {
  return (
      <AuthProvider>
        <Routes>
          {/* Public pages, outside the layout: no sign-in needed to see
              them, and no header or tab bar. (The old email digest
              routes that once headed this list are gone.) */}

          {/* Account deletion, deliberately outside Layout: everything
              inside it shows the login page when signed out, and a page
              explaining how to delete your account has to be readable
              without one. Google Play requires exactly that. */}
          <Route path="/delete-account" element={<DeleteAccountPage />} />
          {/* The pause reminder email's one-tap cancel. Public: the
              link's token is what is trusted, no sign-in. */}
          <Route path="/billing/stop" element={<StopSubscriptionPage />} />
          {/* Gift subscriptions: buying one (no account needed), the
              thank-you page Stripe sends the buyer to, and redeeming one
              (a signed-out visitor signs up or in first; GiftClaim applies it). */}
          <Route path="/gift" element={<GiftPage />} />
          <Route path="/gift/thanks" element={<GiftThanksPage />} />
          <Route path="/gift/redeem" element={<GiftRedeemPage />} />
          {/* The Monday garden email's "monthly instead" and "Unsubscribe"
              links. Public: the link's token is what is trusted. */}
          <Route path="/email/monday" element={<MondayEmailLinkPage />} />
          {/* The page behind a share link. Public: no account, no layout. */}
          <Route path="/s/:code" element={<SharePage />} />
          {/* Gardens on Grow: the public listing of gardens and a plant search. */}
          <Route path="/gardens" element={<GardensPage />} />
          {/* A gardener's public garden page: what "Public" points at. */}
          <Route path="/g/:code" element={<GardenPage />} />
          {/* The gardener's own address (grow.gardengameplan.com/@amy).
              Ranked below every named path, so the app's pages win; a
              segment without "@" goes on to Today (GardenPage). */}
          <Route path="/:handle" element={<GardenPage />} />
          {/* A public plant's or bed's own page (/@amy/cherokee-purple),
              ranked below every named path the same way; anything not
              starting with "@" goes on to Today (GardenItemPage). */}
          <Route path="/:handle/:item" element={<GardenItemPage />} />

          {/* Main app with layout */}
          <Route element={
            <UserConfigProvider>
              <ThemeProvider>
                <WeatherProvider>
                  <TooltipProvider>
                    <Layout />
                  </TooltipProvider>
                </WeatherProvider>
              </ThemeProvider>
            </UserConfigProvider>
          }>
            {/* Home: what to do in the garden today. */}
            <Route path="/" element={<Navigate to="/today" replace />} />
            <Route
              path="/today"
              element={
                // The garden's writes (the type door's add form, Check and save), and the stages, for planting jobs,
                // which move plants on a stage. Today draws no plant list (W24 Job C): the list is read only once an
                // open sheet reads it (listOnDemand), so opening Today or marking a to-do does not download the plants.
                <StagesProvider>
                  <PlantsProvider listOnDemand>
                    <TodayPage />
                  </PlantsProvider>
                </StagesProvider>
              }
            />
            {/* Where a gardener stands with paying, the plans, and the doors
                to Stripe; and where Stripe's checkout sends them back. */}
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/subscribe/success" element={<SubscribeSuccess />} />
            {/* The gallery approval queue: open to anyone signed in, but it
                says "not for you" unless the backend says they approve. */}
            <Route path="/admin/gallery" element={<GalleryQueue />} />
            {/* The admin view-as picker (E16); the server refuses its list to non-admins. */}
            <Route path="/admin/view-as" element={<ViewAsPage />} />
            {/* Where a garden invitation's link lands (?token=…). Inside the
                layout, so a signed-out visitor signs in first; signing in
                keeps this address (homePage) and comes back to it. */}
            <Route path="/garden/join" element={<JoinGarden />} />
            {/* The garden: every plant as a card. */}
            <Route
              path="/plants"
              element={
                <StagesProvider>
                  <PlantsProvider>
                    <PlantGrid />
                  </PlantsProvider>
                </StagesProvider>
              }
            />
            {/* Declared alongside the dynamic plant route; react-router
                ranks the static segment higher, so /plants/archive does not
                get read as a plant id.

                Inside PlantsProvider so that restoring a plant goes through
                the same call the garden uses. Written out separately it
                refreshed the two plant lists and not the outlook, and a
                restored plant came back with last week's advice. */}
            <Route
              path="/plants/archive"
              element={(
                <PlantsProvider>
                  <ArchivedPlants />
                </PlantsProvider>
              )}
            />
            {/* The tray makes plants and puts photos on them, so it needs
                the garden and the stages; it reads the beds' names itself
                (the short list, W24 Job H), so no LocationsProvider. */}
            <Route
              path="/plants/photos"
              element={(
                <StagesProvider>
                  <PlantsProvider>
                    <PhotoTray />
                  </PlantsProvider>
                </StagesProvider>
              )}
            />
            {/* The What to plant tab (D67): the gardener's own plants that can go in now. It draws the server's
                list and links to each plant's page, so it reads no garden provider. */}
            <Route path="/what-to-plant" element={<WhatToPlantPage />} />
            {/* E19 "See them": every crop that can go in now that the garden has none of, with Add. Like What to plant it
                draws the server's answer and reads no garden provider. */}
            <Route path="/what-to-plant/browse" element={<WhatToPlantBrowsePage />} />
            {/* One plant's address (/plants/12): the same garden page,
                scrolled to that plant's card (PlantGrid reads the id). The
                layout's fade treats it as /plants (Layout pageKey). */}
            <Route
              path="/plants/:plantObjId"
              element={
                <StagesProvider>
                  <PlantsProvider>
                    <PlantGrid />
                  </PlantsProvider>
                </StagesProvider>
              }
            />
            {/* The timeline is gone (see dep/). An old bookmark to it would
                fall through to the catch-all at the end and land on Today;
                this sends it to the plant it was about. */}
            <Route
              path="/plants/:plantObjId/timeline"
              element={<Navigate to=".." relative="path" replace />}
            />
            {/* The Seasons tab and Photo sort: /seasons and /photos are the addresses people type. */}
            <Route path="/seasons" element={<Navigate to="/season" replace />} />
            <Route path="/photos" element={<Navigate to="/plants/photos" replace />} />
            <Route
              path="/season"
              element={
                // The garden's season: plants, and stages for the cards it opens.
                <StagesProvider>
                  <PlantsProvider>
                    <SeasonPage />
                  </PlantsProvider>
                </StagesProvider>
              }
            />
            <Route
              path="/season/review"
              element={
                // "Your 2026 season": the same plants and stages Your Seasons reads.
                <StagesProvider>
                  <PlantsProvider>
                    <SeasonReviewPage />
                  </PlantsProvider>
                </StagesProvider>
              }
            />
            <Route
              path="/weather"
              element={
                // Stages too: a plant named under a verdict opens its card
                // here, and the card reads the stage colours.
                <StagesProvider>
                  <PlantsProvider>
                    <WeatherPage />
                  </PlantsProvider>
                </StagesProvider>
              }
            />
            {/* Plants and stages as well as locations: a location card lists
                the plants growing there, and opening one shows the same card
                the garden does — which reads the garden and the stage
                colours. */}
            <Route
              path="/beds/archive"
              element={(
                <StagesProvider>
                  <PlantsProvider>
                    <LocationsProvider>
                      <ArchivedLocations />
                    </LocationsProvider>
                  </PlantsProvider>
                </StagesProvider>
              )}
            />
            {/* The Beds tab (D56: /beds; it was /locations), and one bed open
                (/beds/12): the same page, which draws the list or the bed. */}
            {['/beds', '/beds/:bedId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <StagesProvider>
                    <PlantsProvider>
                      <LocationsProvider>
                        <LocationGrid />
                      </LocationsProvider>
                    </PlantsProvider>
                  </StagesProvider>
                }
              />
            ))}
            {/* The old addresses (plant cards' and Seasons' bed links, emails,
                bookmarks, ?edit=&more=1) go to their /beds ones. */}
            <Route path="/locations" element={<LocationsRedirect />} />
            <Route path="/locations/*" element={<LocationsRedirect />} />
            {/* The profile: a list of rows, each opening a screen named in
                the address (?s=details, ?s=people…). Reads the account and
                its own queries; it needs none of the garden providers. */}
            <Route
              path="/profile"
              element={
                <UserProfile />
              }
            />
            {/* Add other protected routes using Layout here */}
            {/* The catch-all. An address that matched nothing, a typo or a
                page that has gone, used to render an empty white page.
                Inside the layout, so someone signed out sees the login page
                first and lands on Today after. A specific path always ranks
                above "*", so the public pages above are not caught. */}
            <Route path="*" element={<Navigate to="/today" replace />} />
          </Route>
        </Routes>
      </AuthProvider>
  );
}
